import { GaugeCircular } from "@app/components/Progress/ProgressCircular/GaugeCircular";
import { SubthemeTag } from "@app/shared/components/Theme/SubthemeTag";
import { ThemeTag } from "@app/shared/components/Theme/ThemeTag";
import { ProgressBar } from "@design-system/DataDisplay/ProgressBar/ProgressBar";
import { Button } from "@design-system/Inputs/Button";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { GetObjectiveProgressOverviewQuery } from "@generated/client/graphql";
import chroma from "chroma-js";
import { FC, useState } from "react";
import { useObjectiveProgressOverviewTranslations } from "../i18n";

const ThemeProgressCard: FC<{
    theme: GetObjectiveProgressOverviewQuery["objectiveProgressOverview"][number];
    className?: string;
    itemId: string; //needed for scroll menu visibility
}> = ({ theme, className }) => {
    const { t, keys } = useObjectiveProgressOverviewTranslations();
    const [showAllSubthemes, setShowAllSubthemes] = useState(false);

    const maxVisibleSubthemes = 4;
    const hasMoreSubthemes = theme.subthemes.length > maxVisibleSubthemes;
    const visibleSubthemes = showAllSubthemes
        ? theme.subthemes
        : theme.subthemes.slice(0, maxVisibleSubthemes);
    const remainingCount = theme.subthemes.length - maxVisibleSubthemes;

    return (
        <FlexCol
            className={`w-[319px] ${className || ""}`}
            gap="4"
            alignItems="start"
        >
            <FlexCol gap="1" alignItems="start" alignSelf="stretch">
                <ThemeTag themeId={theme.themeId} />
            </FlexCol>
            <FlexRow
                justifyContent="center"
                alignItems="center"
                gap="10"
                alignSelf="stretch"
            >
                <GaugeCircular
                    size={104}
                    progress={theme.percentage}
                    width={6}
                    indicatorColor={theme.themeColor || undefined}
                >
                    <Text variant="body-lg-bold">{theme.percentage}%</Text>
                </GaugeCircular>
            </FlexRow>
            <FlexCol
                justifyContent="end"
                alignItems="start"
                alignSelf="stretch"
            >
                <FlexRow gap="2" w="full" p="1">
                    <FlexCol gap="1" alignItems="start" w="full">
                        <Text variant="caption-bold">{t(keys.subtheme)}</Text>
                    </FlexCol>
                    <FlexCol className="w-[80px]" gap="1" alignItems="end">
                        <Text variant="caption-bold">
                            {t(keys.validated_objectives)}
                        </Text>
                    </FlexCol>
                </FlexRow>

                {visibleSubthemes.map((subtheme) => (
                    <FlexRow
                        key={subtheme.subthemeId}
                        alignItems="center"
                        gap="2"
                        w="full"
                        p="1"
                    >
                        <FlexCol gap="1" alignItems="start" w="full">
                            <SubthemeTag
                                subthemeId={subtheme.subthemeId}
                                themeId={theme.themeId}
                            />
                        </FlexCol>
                        <FlexCol className="w-[80px]" gap="1" alignItems="end">
                            <FlexRow alignItems="center" gap="1">
                                <ProgressBar
                                    segments={[
                                        {
                                            color: chroma(
                                                theme.themeColor ?? "#03A365",
                                            ),
                                            value: subtheme.validatedObjectives,
                                            label: t(keys.validated_objectives),
                                            labelValue:
                                                subtheme.validatedObjectives.toString(),
                                        },
                                    ]}
                                    total={subtheme.totalObjectives}
                                    className="w-[60px]"
                                    hideOnZeroTotal={false}
                                />
                                <Text variant="body-sm" className="text-nowrap">
                                    {t(keys.validated_objectives_count, {
                                        validated: subtheme.validatedObjectives,
                                        total: subtheme.totalObjectives,
                                    })}
                                </Text>
                            </FlexRow>
                        </FlexCol>
                    </FlexRow>
                ))}

                {hasMoreSubthemes && !showAllSubthemes && (
                    <FlexRow justifyContent="start" w="full">
                        <Button
                            variant="text"
                            size="sm"
                            onClick={() => setShowAllSubthemes(true)}
                            className="text-brand"
                        >
                            {t("view_more_subtheme", {
                                count: remainingCount,
                            })}
                        </Button>
                    </FlexRow>
                )}

                {showAllSubthemes && hasMoreSubthemes && (
                    <FlexRow justifyContent="start" w="full">
                        <Button
                            variant="text"
                            size="sm"
                            onClick={() => setShowAllSubthemes(false)}
                            className="text-brand"
                        >
                            {t(keys.view_less_subtheme)}
                        </Button>
                    </FlexRow>
                )}
            </FlexCol>
        </FlexCol>
    );
};

export default ThemeProgressCard;
